import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const ComparisonTable = () => {
  const [expandedCategory, setExpandedCategory] = useState(null);

  const comparisonData = [
    {
      category: 'Core Features',
      features: [
        {
          name: 'Fast POS System',
          cloud: true,
          desktop: true,
          description: 'Quick checkout and transaction processing'
        },
        {
          name: 'Inventory Management',
          cloud: true,
          desktop: true,
          description: 'Track stock levels and product information'
        },
        {
          name: 'Barcode Scanning',
          cloud: true,
          desktop: true,
          description: 'Scan products for quick identification'
        },
        {
          name: 'Receipt Printing',
          cloud: true,
          desktop: true,
          description: 'Print customer receipts'
        }
      ]
    },
    {
      category: 'Connectivity',
      features: [
        {
          name: 'Offline Capability',
          cloud: false,
          desktop: true,
          description: 'Work without internet connection'
        },
        {
          name: 'Real-time Sync',
          cloud: true,
          desktop: false,
          description: 'Instant data synchronization across devices'
        },
        {
          name: 'Manual Cloud Sync',
          cloud: false,
          desktop: true,
          description: 'Sync data to cloud when convenient'
        },
        {
          name: 'Local Data Backup',
          cloud: false,
          desktop: true,
          description: 'Store data locally on your device'
        }
      ]
    },
    /* Multi-store management not listed here yet (feature coming soon) */
    {
      category: 'Reporting & Analytics',
      features: [
        {
          name: 'Daily Sales Reports',
          cloud: true,
          desktop: true,
          description: 'Track daily revenue and transactions'
        },
        {
          name: 'Real-time Analytics',
          cloud: true,
          desktop: false,
          description: 'Live business insights and metrics'
        },
        {
          name: 'Low Stock Alerts',
          cloud: true,
          desktop: true,
          description: 'Get notified when inventory is low'
        },
        {
          name: 'Custom Reports',
          cloud: true,
          desktop: true,
          description: 'Generate tailored business reports'
        }
      ]
    },
    {
      category: 'Payment & Customer',
      features: [
        {
          name: 'Payment Integration',
          cloud: true,
          desktop: true,
          description: 'Accept multiple payment methods'
        },
        {
          name: 'Customer Loyalty',
          cloud: true,
          desktop: true,
          description: 'Reward repeat customers'
        },
        {
          name: 'Customer Database',
          cloud: true,
          desktop: true,
          description: 'Store customer information'
        },
        {
          name: 'Split Payments',
          cloud: true,
          desktop: true,
          description: 'Accept multiple payment types per transaction'
        }
      ]
    },
    {
      category: 'User Management',
      features: [
        {
          name: 'User Permissions',
          cloud: true,
          desktop: true,
          description: 'Control staff access levels'
        },
        {
          name: 'Activity Tracking',
          cloud: true,
          desktop: true,
          description: 'Monitor staff actions and sales'
        },
        {
          name: 'Multi-user Support',
          cloud: true,
          desktop: true,
          description: 'Multiple staff members can use system'
        },
        {
          name: 'Role-based Access',
          cloud: true,
          desktop: true,
          description: 'Assign roles with specific permissions'
        }
      ]
    }
  ];

  const toggleCategory = (index) => {
    setExpandedCategory(expandedCategory === index ? null : index);
  };

  return (
    <div className="w-full">
      {/* Desktop Table View */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-border">
              <th className="py-4 px-6 text-left font-heading text-lg font-semibold text-foreground">
                Feature
              </th>
              <th className="py-4 px-6 text-center font-heading text-lg font-semibold text-foreground">
                Cloud Solution
              </th>
              <th className="py-4 px-6 text-center font-heading text-lg font-semibold text-foreground">
                Desktop POS
              </th>
            </tr>
          </thead>
          <tbody>
            {comparisonData?.map((category, categoryIndex) => (
              <React.Fragment key={categoryIndex}>
                <tr className="bg-muted/50">
                  <td
                    colSpan="3"
                    className="py-3 px-6 font-heading text-base font-semibold text-foreground"
                  >
                    {category?.category}
                  </td>
                </tr>
                {category?.features?.map((feature, featureIndex) => (
                  <tr
                    key={featureIndex}
                    className="border-b border-border transition-smooth hover:bg-muted/30"
                  >
                    <td className="py-4 px-6">
                      <div>
                        <p className="font-body text-base text-foreground mb-1">
                          {feature?.name}
                        </p>
                        <p className="font-body text-sm text-muted-foreground">
                          {feature?.description}
                        </p>
                      </div>
                    </td>
                    <td className="py-4 px-6 text-center">
                      {feature?.cloud ? (
                        <Icon
                          name="Check"
                          size={24}
                          color="var(--color-success)"
                          strokeWidth={2}
                          className="mx-auto"
                        />
                      ) : (
                        <Icon
                          name="X"
                          size={24}
                          color="var(--color-muted-foreground)"
                          strokeWidth={2}
                          className="mx-auto"
                        />
                      )}
                    </td>
                    <td className="py-4 px-6 text-center">
                      {feature?.desktop ? (
                        <Icon
                          name="Check"
                          size={24}
                          color="var(--color-success)"
                          strokeWidth={2}
                          className="mx-auto"
                        />
                      ) : (
                        <Icon
                          name="X"
                          size={24}
                          color="var(--color-muted-foreground)"
                          strokeWidth={2}
                          className="mx-auto"
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Accordion View */}
      <div className="lg:hidden space-y-4">
        {comparisonData?.map((category, categoryIndex) => (
          <div
            key={categoryIndex}
            className="rounded-xl border border-border bg-card overflow-hidden"
          >
            <button
              onClick={() => toggleCategory(categoryIndex)}
              className="w-full flex items-center justify-between p-4 md:p-6 transition-smooth hover:bg-muted/30 focus:outline-none"
            >
              <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground">
                {category?.category}
              </h3>
              <Icon
                name={expandedCategory === categoryIndex ? 'ChevronUp' : 'ChevronDown'}
                size={24}
                color="var(--color-foreground)"
                strokeWidth={2}
              />
            </button>

            {expandedCategory === categoryIndex && (
              <div className="border-t border-border p-4 md:p-6 space-y-6">
                {category?.features?.map((feature, featureIndex) => (
                  <div key={featureIndex} className="space-y-3">
                    <div>
                      <p className="font-body text-base font-medium text-foreground mb-1">
                        {feature?.name}
                      </p>
                      <p className="font-body text-sm text-muted-foreground">
                        {feature?.description}
                      </p>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="flex items-center gap-2">
                        <span className="font-body text-sm text-muted-foreground">
                          Cloud:
                        </span>
                        {feature?.cloud ? (
                          <Icon
                            name="Check"
                            size={20}
                            color="var(--color-success)"
                            strokeWidth={2}
                          />
                        ) : (
                          <Icon
                            name="X"
                            size={20}
                            color="var(--color-muted-foreground)"
                            strokeWidth={2}
                          />
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-body text-sm text-muted-foreground">
                          Desktop:
                        </span>
                        {feature?.desktop ? (
                          <Icon
                            name="Check"
                            size={20}
                            color="var(--color-success)"
                            strokeWidth={2}
                          />
                        ) : (
                          <Icon
                            name="X"
                            size={20}
                            color="var(--color-muted-foreground)"
                            strokeWidth={2}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ComparisonTable;